import { Icons } from '@assets/svg'
import { colors, theme } from '@config/theme'
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material'
import { GoogleMap, LoadScript, OverlayView } from '@react-google-maps/api'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import ReactDOMServer from 'react-dom/server'
import styled from 'styled-components'

const libraries: ('places' | 'drawing' | 'geometry' | 'localContext' | 'visualization' | 'marker')[] = [
  'places',
  'marker',
]

const CustomPopup = ({
  position,
  onClose,
  content,
  map,
  id,
}: {
  position: google.maps.LatLngLiteral
  onClose: () => void
  content: ReactNode
  map: google.maps.Map | null
  id: number
}) => {
  // Define possible positions for the tooltip
  const [tooltipPosition, setTooltipPosition] = useState<'bottom' | 'top' | 'left' | 'right'>('bottom')
  const tooltipRef = useRef<HTMLDivElement>(null)

  // Check marker position relative to map bounds and adjust tooltip position
  const checkPositionRelativeToBounds = useCallback(() => {
    if (!map) {
      return
    }

    try {
      // Get map bounds
      const bounds = map.getBounds()
      if (!bounds) {
        return
      }

      // Get bounds coordinates
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()

      const north = ne.lat()
      const east = ne.lng()
      const south = sw.lat()
      const west = sw.lng()

      // Calculate dimensions
      const totalHeight = north - south
      const totalWidth = east - west

      // Calculate distance of marker from edges (as ratio of total size)
      const bottomRatio = (position.lat - south) / totalHeight
      const topRatio = (north - position.lat) / totalHeight
      const leftRatio = (position.lng - west) / totalWidth
      const rightRatio = (east - position.lng) / totalWidth

      // Determine the closest edge (smallest ratio means closest to that edge)
      const minRatio = Math.min(bottomRatio, topRatio, leftRatio, rightRatio)

      // Set position based on the edge the marker is closest to
      if (minRatio === bottomRatio && bottomRatio < 0.15) {
        setTooltipPosition('top') // If close to bottom, show tooltip on top
      } else if (minRatio === topRatio && topRatio < 0.15) {
        setTooltipPosition('bottom') // If close to top, show tooltip on bottom
      } else if (minRatio === leftRatio && leftRatio < 0.15) {
        setTooltipPosition('right') // If close to left, show tooltip on right
      } else if (minRatio === rightRatio && rightRatio < 0.15) {
        setTooltipPosition('left') // If close to right, show tooltip on left
      } else {
        // Default position if not near any edge
        setTooltipPosition('bottom')
      }
    } catch (error) {
      console.error('Error calculating tooltip position:', error)
      setTooltipPosition('bottom')
    }
  }, [map, position])

  // Run position check when map or position changes
  useEffect(() => {
    if (!map) {
      return
    }
    checkPositionRelativeToBounds()
  }, [map, position, checkPositionRelativeToBounds])

  // Update position when map bounds change
  useEffect(() => {
    if (!map) {
      return
    }

    const boundsListener = map.addListener('bounds_changed', checkPositionRelativeToBounds)

    return () => {
      google.maps.event.removeListener(boundsListener)
    }
  }, [map, checkPositionRelativeToBounds])

  // Generate styles based on tooltip position
  const getTooltipStyles = () => {
    switch (tooltipPosition) {
      case 'bottom':
        return {
          transform: 'translateX(-50%)',
          margin: '0 0 0 0',
        }
      case 'top':
        return {
          transform: 'translateX(-50%) translateY(-100%)',
          margin: '-60px 0 0 0',
        }
      case 'left':
        return {
          transform: 'translateY(-100%) translateX(-100%)',
          margin: '0 0 0 -24px',
        }
      case 'right':
        return {
          transform: 'translateY(-100%)',
          margin: '0 0 0 24px',
        }
      default:
        return {
          transform: 'translateX(-50%)',
          margin: '0 0 0 0',
        }
    }
  }

  const tooltipStyles = getTooltipStyles()

  return (
    <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
      <Box
        ref={tooltipRef}
        sx={{
          backgroundColor: '#fff',
          padding: '12px',
          borderRadius: '12px',
          border: '1px solid #D88EC8',
          boxShadow: '0px 24px 48px -12px rgba(16, 24, 40, 0.38);',
          minWidth: '200px',
          position: 'relative',
          fontFamily: 'Inter, sans-serif',
          transform: tooltipStyles.transform,
          margin: tooltipStyles.margin,
        }}
        data-testid={`map-marker-pop-up-window-address-${id}`}
      >
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            color: colors.brandGray700,
            padding: '2px',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
            <path
              d="M15.5469 5.17413L5.54688 15.1741M5.54688 5.17413L15.5469 15.1741"
              stroke="#344054"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
        {content}
      </Box>
    </OverlayView>
  )
}

const MapViewComponent = ({ camps }: { camps: any[] }) => {
  const isLargeScreen: boolean = useMediaQuery(theme.device['md'])

  const mapRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([])
  const hasLoadedRef = useRef(false)
  const markers = markersRef.current
  const selectedMarkerIdRef = useRef<number | null>(null)
  const [selectedPoint, setSelectedPoint] = useState<any | null>(null)

  // State for map center and zoom (for test attributes)
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [mapZoom, setMapZoom] = useState<number | null>(null)

  // Function to handle user location
  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          if (mapRef.current) {
            mapRef.current.setCenter(coords)
            mapRef.current.setZoom(14)
          }
        },
        (error) => {
          alert(`Error getting location: ${error.message}`)
        }
      )
    } else {
      alert('Geolocation is not supported by your browser.')
    }
  }

  const handleMapLoad = async (map: google.maps.Map) => {
    mapRef.current = map

    if (!window.google?.maps?.marker) {
      console.error('AdvancedMarkerElement is not available.')
      return
    }

    const { AdvancedMarkerElement } = window.google.maps.marker
    const bounds = new window.google.maps.LatLngBounds()

    for (const point of camps) {
      if (!point.dirty && point.lat && point.lng) {
        const position = { lat: point.lat, lng: point.lng }

        const getMarkerSVG = (isSelected: boolean) =>
          ReactDOMServer.renderToStaticMarkup(
            <Icons.MapMarkerIcon
              colors={isSelected ? ['#A44890', '#A44890', '#fff'] : ['#FFE6FA', '#D88EC8', '#101828']}
            />
          )
        const isSelected = selectedMarkerIdRef.current === point.id
        const svgString = getMarkerSVG(isSelected)
        const parser = new DOMParser()
        const svgElement = parser.parseFromString(svgString, 'image/svg+xml').documentElement

        const marker = new AdvancedMarkerElement({
          position,
          map,
          content: svgElement,
          title: point.title,
        })

        svgElement.setAttribute('data-testid', `map-marker-address-${point.id}`)

        // Add click listener to the marker
        marker.addListener('gmp-click', () => {
          selectedMarkerIdRef.current = point.id
          setSelectedPoint(point)

          // Update all markers' SVGs
          markers.forEach((m, index) => {
            const markerPoint = camps[index]
            const isSelected = markerPoint.id === point.id
            const updatedSvgString = getMarkerSVG(isSelected)
            const updatedSvg = new DOMParser().parseFromString(updatedSvgString, 'image/svg+xml').documentElement
            updatedSvg.setAttribute('data-testid', `map-marker-address-${markerPoint.id}`)
            m.content = updatedSvg
          })
        })

        markers.push(marker)
        bounds.extend(position)
      } else {
        console.warn('Invalid point:', point)
      }
    }
    if (!bounds.isEmpty() && !hasLoadedRef.current) {
      map.fitBounds(bounds)

      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom()
        if (currentZoom !== undefined) {
          map.setZoom(currentZoom + 1)
        }
      })

      hasLoadedRef.current = true
    }

    // Create the clusterer with AdvancedMarkerElement markers
    new MarkerClusterer({
      map,
      markers,
      algorithm: new SuperClusterAlgorithm({
        minPoints: 5,
      }),
      renderer: {
        render: ({ count, position }) => {
          const div = document.createElement('div')

          div.style.color = '#000'
          div.style.fontSize = '14px'
          div.style.fontFamily = 'Inter, sans-serif'
          div.style.fontWeight = 'bold'
          div.style.borderRadius = '50%'
          div.style.padding = '12px'
          div.style.display = 'flex'
          div.style.alignItems = 'center'
          div.style.justifyContent = 'center'
          div.style.border = '2px solid #D88EC8'
          div.style.boxShadow = '0 0 8px rgba(0,0,0,0.3)'
          div.style.width = '40px'
          div.style.height = '40px'
          div.style.backgroundColor = '#FFE6FA'
          div.textContent = count.toString()

          div.setAttribute('data-testid', `marker-cluster`)

          return new window.google.maps.marker.AdvancedMarkerElement({
            position,
            content: div,
          })
        },
      },
    })
  }

  // Update map center/zoom on map events
  const handleMapIdle = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter()
      const zoom = mapRef.current.getZoom()
      if (center && typeof zoom === 'number') {
        setMapCenter({ lat: center.lat(), lng: center.lng() })
        setMapZoom(zoom)
      }
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: isLargeScreen ? '700px' : '600px',
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
      data-testid="map-view-component"
      data-map-center-lat={mapCenter ? mapCenter.lat.toFixed(2) : undefined}
      data-map-center-lng={mapCenter ? mapCenter.lng.toFixed(2) : undefined}
      data-map-zoom={mapZoom !== null ? mapZoom : undefined}
    >
      {/* Locate Me Button */}
      <IconButton
        onClick={handleLocateMe}
        sx={{
          position: 'absolute',
          bottom: 116,
          right: 10,
          zIndex: 10,
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          background: '#fff',
          border: '1px solid #A9B6FC',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          '&:hover': { background: '#f5f5f5' },
        }}
        aria-label="Locate Me"
        data-testid="user-location-button"
      >
        <Icons.NavigationArrowIcon color={colors.brandGray700} size={22} />
      </IconButton>
      <LoadScript googleMapsApiKey="AIzaSyBKdf6YcE17EJKdVK4nsA6MPrrMpRFw520" libraries={libraries as any}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          onLoad={handleMapLoad}
          onIdle={handleMapIdle}
          {...(camps.length === 0 && { center: { lat: 41, lng: -101 }, zoom: 4 })}
          options={{
            mapId: 'YOUR_MAP_ID',
            // gestureHandling: 'greedy',
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: true,
            mapTypeControl: false,
            clickableIcons: false,
            cameraControl: false,
            keyboardShortcuts: false,
            // styles: [
            //   {
            //     featureType: 'poi',
            //     stylers: [{ visibility: 'off' }],
            //   },
            //   // {
            //   //   featureType: 'transit',
            //   //   stylers: [{ visibility: 'off' }],
            //   // },
            //   // {
            //   //   featureType: 'road',
            //   //   elementType: 'labels',
            //   //   stylers: [{ visibility: 'off' }],
            //   // },
            // ],
          }}
        >
          {selectedPoint && (
            <CustomPopup
              id={selectedPoint.id}
              position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
              map={mapRef.current}
              onClose={() => {
                setSelectedPoint(null)
                selectedMarkerIdRef.current = null

                // Revert all marker colors
                markers.forEach((m: google.maps.marker.AdvancedMarkerElement, index: number) => {
                  const markerPoint = camps[index]
                  const updatedSvgString = ReactDOMServer.renderToStaticMarkup(
                    <Icons.MapMarkerIcon colors={['#FFE6FA', '#D88EC8', '#101828']} />
                  )

                  const updatedSvg = new DOMParser().parseFromString(updatedSvgString, 'image/svg+xml').documentElement
                  updatedSvg.setAttribute('data-testid', `map-marker-${markerPoint.id}`)
                  m.content = updatedSvg
                })
              }}
              content={
                <div>
                  <StyledPopupTitle>{`Address ${selectedPoint.id}`}</StyledPopupTitle>
                  <StyledPopupText>Popup content here.</StyledPopupText>
                </div>
              }
            />
          )}
        </GoogleMap>
      </LoadScript>
    </Box>
  )
}

export default MapViewComponent

const StyledPopupTitle = styled(Typography)`
  color: ${colors.brandGray900};
  font-size: 1rem !important;
  line-height: 1.5rem !important;
  font-weight: 600 !important;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`

const StyledPopupText = styled(Typography)`
  color: ${colors.brandGray700};
  font-size: 0.875rem !important;
  line-height: 1.25rem !important;
  font-weight: 400 !important;
`
